import { useState, useEffect, useCallback, useRef } from 'react';
import {
  signalRConnection,
  ConnectionStatus,
  SignalREventHandlers,
  LocationUpdateMessage,
  HuntStateChangedMessage,
  ParticipantJoinedMessage,
  ParticipantLeftMessage,
  ParticipantRemovedMessage,
  ParticipantReadyMessage,
  PinCreatedMessage,
  PinDeletedMessage,
  PinDiscoveredMessage,
  ItemFoundMessage,
  HunterCompletedMessage,
  HuntCompletedMessage,
  HuntDeletedMessage,
  ConnectionStatusMessage,
  ChallengeEncounteredMessage,
  ChallengeAttemptMessage,
  ChallengeSolvedMessage,
  HuntStartingMessage,
  StartCancelledMessage,
  ValidationRequestedMessage,
  ValidationApprovedMessage,
  ValidationApprovedWithChallengeMessage,
  ValidationRejectedMessage,
} from '../services/signalr/connection';
import { useLocationStore } from '../stores/locationStore';

interface UseSignalROptions {
  huntId?: string;
  onLocationUpdate?: (message: LocationUpdateMessage) => void;
  onHuntStateChanged?: (message: HuntStateChangedMessage) => void;
  onParticipantJoined?: (message: ParticipantJoinedMessage) => void;
  onParticipantLeft?: (message: ParticipantLeftMessage) => void;
  onParticipantRemoved?: (message: ParticipantRemovedMessage) => void;
  onParticipantReady?: (message: ParticipantReadyMessage) => void;
  onPinCreated?: (message: PinCreatedMessage) => void;
  onPinDeleted?: (message: PinDeletedMessage) => void;
  onPinDiscovered?: (message: PinDiscoveredMessage) => void;
  onItemFound?: (message: ItemFoundMessage) => void;
  onHunterCompleted?: (message: HunterCompletedMessage) => void;
  onHuntCompleted?: (message: HuntCompletedMessage) => void;
  onHuntDeleted?: (message: HuntDeletedMessage) => void;
  onConnectionStatusChanged?: (message: ConnectionStatusMessage) => void;
  // Challenge event handlers
  onChallengeEncountered?: (message: ChallengeEncounteredMessage) => void;
  onChallengeAttempt?: (message: ChallengeAttemptMessage) => void;
  onChallengeSolved?: (message: ChallengeSolvedMessage) => void;
  // Hunt starting/cancelled handlers
  onHuntStarting?: (message: HuntStartingMessage) => void;
  onStartCancelled?: (message: StartCancelledMessage) => void;
  // Validation handlers - for hider referee feature
  onValidationRequested?: (message: ValidationRequestedMessage) => void;
  onValidationApproved?: (message: ValidationApprovedMessage) => void;
  onValidationApprovedWithChallenge?: (message: ValidationApprovedWithChallengeMessage) => void;
  onValidationRejected?: (message: ValidationRejectedMessage) => void;
  // Reconnection handler - called after successful reconnect to re-fetch state
  onReconnected?: () => void;
}

interface UseSignalRReturn {
  connectionStatus: ConnectionStatus;
  isConnected: boolean;
  connect: (huntId: string) => Promise<boolean>;
  disconnect: () => Promise<void>;
  forceReconnect: () => Promise<boolean>;
  sendLocation: (
    huntId: string,
    location: {
      latitude: number;
      longitude: number;
      accuracy: number;
      heading?: number | null;
      speed?: number | null;
    }
  ) => Promise<boolean>;
}

/**
 * React hook for SignalR real-time connection
 * Can be used with or without options
 */
export function useSignalR(options?: UseSignalROptions): UseSignalRReturn {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const handlersRef = useRef(options || {});
  const updateHunterLocation = useLocationStore((state) => state.updateHunterLocation);

  // Update handlers ref SYNCHRONOUSLY on every render to avoid race conditions
  // Using useEffect was causing the ref to be out of sync when SignalR messages arrived
  handlersRef.current = options || {};

  // Subscribe to connection status changes
  useEffect(() => {
    const unsubscribe = signalRConnection.onStatusChange(setConnectionStatus);
    return unsubscribe;
  }, []);

  // Subscribe to reconnection events for state re-fetch
  useEffect(() => {
    const onReconnectedHandler = handlersRef.current.onReconnected;
    if (!onReconnectedHandler) return;
    
    const unsubscribe = signalRConnection.onReconnected(() => {
      handlersRef.current.onReconnected?.();
    });
    return unsubscribe;
  }, []);

  // Connect to SignalR
  // Build handlers object dynamically - ONLY include handlers that are defined in options
  // This prevents one component from overwriting another's handlers during merge
  const connect = useCallback(async (huntId: string): Promise<boolean> => {
    // Always include location handler (updates global store)
    const handlers: Record<string, (msg: unknown) => void> = {
      onLocationUpdate: (msg) => {
        updateHunterLocation(msg as LocationUpdateMessage);
        handlersRef.current.onLocationUpdate?.(msg as LocationUpdateMessage);
      },
    };
    
    // Only add wrappers for handlers that are defined in options
    // This way, a component without onChallengeEncountered won't overwrite one that has it
    const opts = handlersRef.current;
    
    // Debug: log which handlers are being included
    const handlerNames = Object.keys(opts).filter(k => typeof (opts as Record<string, unknown>)[k] === 'function');
    console.log('[useSignalR] connect() called, handlers in options:', handlerNames);
    console.log('[useSignalR] onChallengeEncountered in opts?', !!opts.onChallengeEncountered);
    
    if (opts.onHuntStateChanged) handlers.onHuntStateChanged = (msg) => handlersRef.current.onHuntStateChanged?.(msg as HuntStateChangedMessage);
    if (opts.onParticipantJoined) handlers.onParticipantJoined = (msg) => handlersRef.current.onParticipantJoined?.(msg as ParticipantJoinedMessage);
    if (opts.onParticipantLeft) handlers.onParticipantLeft = (msg) => handlersRef.current.onParticipantLeft?.(msg as ParticipantLeftMessage);
    if (opts.onParticipantRemoved) handlers.onParticipantRemoved = (msg) => handlersRef.current.onParticipantRemoved?.(msg as ParticipantRemovedMessage);
    if (opts.onParticipantReady) handlers.onParticipantReady = (msg) => handlersRef.current.onParticipantReady?.(msg as ParticipantReadyMessage);
    if (opts.onPinCreated) handlers.onPinCreated = (msg) => handlersRef.current.onPinCreated?.(msg as PinCreatedMessage);
    if (opts.onPinDeleted) handlers.onPinDeleted = (msg) => handlersRef.current.onPinDeleted?.(msg as PinDeletedMessage);
    if (opts.onPinDiscovered) handlers.onPinDiscovered = (msg) => {
      console.log('[useSignalR] onPinDiscovered wrapper called, handler exists:', !!handlersRef.current.onPinDiscovered);
      handlersRef.current.onPinDiscovered?.(msg as PinDiscoveredMessage);
    };
    if (opts.onItemFound) handlers.onItemFound = (msg) => handlersRef.current.onItemFound?.(msg as ItemFoundMessage);
    if (opts.onHunterCompleted) handlers.onHunterCompleted = (msg) => handlersRef.current.onHunterCompleted?.(msg as HunterCompletedMessage);
    if (opts.onHuntCompleted) handlers.onHuntCompleted = (msg) => {
      console.log('[useSignalR] onHuntCompleted wrapper called, handler exists:', !!handlersRef.current.onHuntCompleted);
      handlersRef.current.onHuntCompleted?.(msg as HuntCompletedMessage);
    };
    if (opts.onHuntDeleted) handlers.onHuntDeleted = (msg) => handlersRef.current.onHuntDeleted?.(msg as HuntDeletedMessage);
    if (opts.onConnectionStatusChanged) handlers.onConnectionStatusChanged = (msg) => handlersRef.current.onConnectionStatusChanged?.(msg as ConnectionStatusMessage);
    
    // Challenge handlers
    if (opts.onChallengeEncountered) handlers.onChallengeEncountered = (msg) => {
      console.log('[useSignalR] onChallengeEncountered wrapper called');
      console.log('[useSignalR] handlersRef.current.onChallengeEncountered exists:', !!handlersRef.current.onChallengeEncountered);
      handlersRef.current.onChallengeEncountered?.(msg as ChallengeEncounteredMessage);
    };
    if (opts.onChallengeAttempt) handlers.onChallengeAttempt = (msg) => {
      console.log('[useSignalR] onChallengeAttempt wrapper called');
      handlersRef.current.onChallengeAttempt?.(msg as ChallengeAttemptMessage);
    };
    if (opts.onChallengeSolved) handlers.onChallengeSolved = (msg) => {
      console.log('[useSignalR] onChallengeSolved wrapper called');
      handlersRef.current.onChallengeSolved?.(msg as ChallengeSolvedMessage);
    };
    
    // Hunt starting/cancelled handlers
    if (opts.onHuntStarting) handlers.onHuntStarting = (msg) => handlersRef.current.onHuntStarting?.(msg as HuntStartingMessage);
    if (opts.onStartCancelled) handlers.onStartCancelled = (msg) => handlersRef.current.onStartCancelled?.(msg as StartCancelledMessage);
    
    // Validation handlers - for hider referee feature
    if (opts.onValidationRequested) handlers.onValidationRequested = (msg) => handlersRef.current.onValidationRequested?.(msg as ValidationRequestedMessage);
    if (opts.onValidationApproved) handlers.onValidationApproved = (msg) => handlersRef.current.onValidationApproved?.(msg as ValidationApprovedMessage);
    if (opts.onValidationApprovedWithChallenge) handlers.onValidationApprovedWithChallenge = (msg) => handlersRef.current.onValidationApprovedWithChallenge?.(msg as ValidationApprovedWithChallengeMessage);
    if (opts.onValidationRejected) handlers.onValidationRejected = (msg) => handlersRef.current.onValidationRejected?.(msg as ValidationRejectedMessage);

    console.log('[useSignalR] Passing handlers to connection:', Object.keys(handlers));
    return signalRConnection.connect(huntId, handlers as SignalREventHandlers);
  }, [updateHunterLocation]);

  // Disconnect from SignalR
  const disconnect = useCallback(async (): Promise<void> => {
    await signalRConnection.disconnect();
  }, []);

  // Force reconnect (for manual retry)
  const forceReconnect = useCallback(async (): Promise<boolean> => {
    return signalRConnection.forceReconnect();
  }, []);

  // Send location update
  const sendLocation = useCallback(
    async (
      _huntId: string,
      location: {
        latitude: number;
        longitude: number;
        accuracy: number;
        heading?: number | null;
        speed?: number | null;
      }
    ): Promise<boolean> => {
      return signalRConnection.sendLocation({
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        heading: location.heading ?? undefined,
        speed: location.speed ?? undefined,
      });
    },
    []
  );

  return {
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    connect,
    disconnect,
    forceReconnect,
    sendLocation,
  };
}

export type {
  LocationUpdateMessage,
  HuntStateChangedMessage,
  ParticipantJoinedMessage,
  ParticipantLeftMessage,
  ParticipantRemovedMessage,
  ParticipantReadyMessage,
  PinCreatedMessage,
  PinDeletedMessage,
  PinDiscoveredMessage,
  ItemFoundMessage,
  HunterCompletedMessage,
  HuntCompletedMessage,
  HuntDeletedMessage,
  ConnectionStatusMessage,
  ChallengeEncounteredMessage,
  ChallengeAttemptMessage,
  ChallengeSolvedMessage,
  HuntStartingMessage,
  StartCancelledMessage,
  ValidationRequestedMessage,
  ValidationApprovedMessage,
  ValidationApprovedWithChallengeMessage,
  ValidationRejectedMessage,
};
